import { type KeyboardEvent, type ReactNode, useRef } from 'react'
import { cn } from '@/shared/lib'

// Табы секций страницы. Presentational: значение/смена наружу (страница синхронит
// со search-param `?tab=`). Роль tablist + стрелки клавиатуры для доступности.

export interface TabItem<V extends string> {
  value: V
  label: string
  count?: number
  // Произвольный бейдж после лейбла — когда числа-каунта недостаточно.
  badge?: ReactNode
}

interface TabsProps<V extends string> {
  items: TabItem<V>[]
  value: V
  onChange: (value: V) => void
  className?: string
  'aria-label'?: string
}

export function Tabs<V extends string>({
  items,
  value,
  onChange,
  className,
  'aria-label': ariaLabel,
}: TabsProps<V>) {
  const listRef = useRef<HTMLDivElement>(null)

  function onKeyDown(e: KeyboardEvent<HTMLElement>) {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
    e.preventDefault()
    const idx = items.findIndex((it) => it.value === value)
    const delta = e.key === 'ArrowRight' ? 1 : -1
    const next = items[(idx + delta + items.length) % items.length]
    if (next) {
      onChange(next.value)
      listRef.current?.querySelector<HTMLButtonElement>(`[data-value="${next.value}"]`)?.focus()
    }
  }

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={ariaLabel}
      className={cn('inline-flex items-center gap-1 rounded-pill bg-bg-elevated p-1', className)}
    >
      {items.map((item) => {
        const active = item.value === value
        return (
          <button
            key={item.value}
            type="button"
            role="tab"
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            data-value={item.value}
            onClick={() => onChange(item.value)}
            onKeyDown={onKeyDown}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-pill px-3.5 py-1.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent',
              active
                ? 'bg-bg-surface text-text-primary'
                : 'text-text-secondary hover:text-text-primary',
            )}
          >
            {item.label}
            {item.count != null && (
              <span className="mono-num text-xs text-text-tertiary">{item.count}</span>
            )}
            {item.badge}
          </button>
        )
      })}
    </div>
  )
}
